import React, { useEffect, useState, useCallback } from "react";
import ChangeOrderItems from "./ChangeOrderItems";
import ChangeOrders from "./ChangeOrders";
import ChangeOrderTasks from "./ChangeOrderTasks";
import TopNav from "../../TopNav/TopNav";
import JobGeneralOverview from "../JobOverviewBar";
import CheckoutNotification from "../checkoutNotification";
import { selectUserDetails, selectUserRoles } from "../../../redux/slices/userSlice";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useMsal } from "@azure/msal-react";
import { useSelector, useDispatch } from "react-redux";
import JobNav from "../JobNav";
import useJobHandler from "../../../functions/JobHandler";
import { fetchChangeOrderRecipients } from "../../../redux/slices/changeOrderSlice";
import { selectGlobalVariables } from "../../../redux/slices/globalVariablesSlice"; // Import the selector for global variables
import "./ChangeOrdersResponsive.css";
import { useSearchParams } from 'react-router-dom';

const ChangeOrderContainer = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const visibleComponent = ['items', 'tasks'].includes(requestedTab) ? requestedTab : 'orders';
  const setVisibleComponent = (tab) => {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('tab', tab);
      return next;
    });
  };
  const { instance, accounts } = useMsal();
  const userDetails = useSelector(selectUserDetails);
  const currentUser = userDetails?.name;
  const dispatch = useDispatch();
  const changeOrderDetailsFromJobDetails = useSelector(state => state.jobDetails.jobDetails.changeOrderDetails || []);
  const globalVariables = useSelector(selectGlobalVariables); // Fetch global variables

  const changeOrderGuidelinesUrl = React.useMemo(() => {
    const changeOrderGuidelinesVariable = globalVariables.find(
      (variable) => variable.Title === "Applications Change Order Link"
    );
    return changeOrderGuidelinesVariable?.VariableValue || "";
  }, [globalVariables]);

  const userRoles = useSelector(selectUserRoles); // User details
  const [roles, setRoles] = useState(mapUserRoles(userRoles));
  
  // State to track deleted items across component switches
  const [deletedItemsIds, setDeletedItemsIds] = useState(new Set());

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  useJobHandler(); // Call the useJobHandler function

  // Function to handle recording a deleted item
  const handleItemDeleted = useCallback((itemId) => {
    setDeletedItemsIds(prev => {
      const newSet = new Set(prev);
      newSet.add(itemId);
      return newSet;
    });
  }, []);

  // Fetch Change Order Recipients when the component mounts
  useEffect(() => {
    // Fetch Change Order Recipients
    dispatch(fetchChangeOrderRecipients({ instance, accounts }));
  }, [dispatch, instance, accounts]);

  // Verify deleted items against Redux state (no longer need to call API)
  useEffect(() => {
    if (deletedItemsIds.size === 0 || changeOrderDetailsFromJobDetails.length === 0) {
      return;
    }
    
    // Create a set of IDs that exist in Redux state
    const reduxItemIds = new Set(changeOrderDetailsFromJobDetails.map(item => item.ID));
    
    // Check if any of our deleted items actually still exist in Redux
    let needsUpdate = false;
    const updatedDeletedItems = new Set();
    
    deletedItemsIds.forEach(id => {
      if (!reduxItemIds.has(id)) {
        updatedDeletedItems.add(id); // Item is truly deleted, keep it in our set
      } else {
        needsUpdate = true; // We found an item we thought was deleted but isn't
      }
    });
    
    // Only update state if there's a difference
    if (needsUpdate) {
      setDeletedItemsIds(updatedDeletedItems);
    }
  }, [changeOrderDetailsFromJobDetails, deletedItemsIds]);

  return (
    <div className="change-order-portal">
      <TopNav />
      <JobGeneralOverview />
      <CheckoutNotification currentUser={currentUser} />
      <JobNav keyValue="changeOrders" />
      <div className="p-3 change-order-page">
        <h2>Change Orders</h2>
        <div className="d-flex justify-content-center mb-3 change-order-portal-nav">
          {(roles.Admin || roles.Apps) && (
            <button
              type="button"
              className="btn btn-primary mx-2 change-order-portal-nav-button"
              onClick={() => setVisibleComponent("items")}
            >
              Create Change Order
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary mx-2 change-order-portal-nav-button"
            onClick={() => setVisibleComponent("orders")}
          >
            Change Orders
          </button>
          <button
            type="button"
            className="btn btn-primary mx-2 change-order-portal-nav-button"
            onClick={() => setVisibleComponent("tasks")}
          >
            Change Order Tasks
          </button>
          {(roles.Admin || roles.Apps) && (
            <a 
              href={changeOrderGuidelinesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-info mx-2 change-order-portal-nav-button"
            >
              Change Order Guidelines
            </a>
          )}
        </div>
        {visibleComponent === "items" && (roles.Admin || roles.Apps) && 
          <ChangeOrderItems onItemDeleted={handleItemDeleted} deletedItemsIds={deletedItemsIds} />
        }
        {visibleComponent === "orders" && 
          <ChangeOrders onItemDeleted={handleItemDeleted} deletedItemsIds={deletedItemsIds} />
        }
        {visibleComponent === "tasks" && 
          <ChangeOrderTasks deletedItemsIds={deletedItemsIds} />
        }
      </div>
    </div>
  );
};

export default ChangeOrderContainer;
